import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import {
  getDocuments,
  getReports,
} from "../api/documentApi";
import {
  getEvidence,
  getEvidenceSummary,
} from "../api/evidenceApi";

function normalizeArray(payload, preferredKey) {
  if (Array.isArray(payload)) return payload;
  if (preferredKey && Array.isArray(payload?.[preferredKey])) {
    return payload[preferredKey];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function safeDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Date unavailable"
    : date.toLocaleDateString();
}

function formatStatus(value) {
  return String(value || "draft")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getReportScore(report) {
  const candidates = [
    report?.scorecard?.overallScore,
    report?.overallScore,
    report?.esgScore,
    report?.readinessScore,
  ];

  const score = candidates
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value));

  return Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : null;
}

function getDocumentDisplayType(document) {
  return (
    document?.detectedType ||
    document?.selectedType ||
    document?.documentType ||
    "Document"
  );
}

export default function CustomerDashboardPage() {
  const [reports, setReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [evidenceSummary, setEvidenceSummary] = useState({
    total: 0,
    pendingReview: 0,
    approved: 0,
    rejected: 0,
    attached: 0,
    published: 0,
    averageConfidence: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    const results = await Promise.allSettled([
      getReports(),
      getDocuments(),
      getEvidenceSummary(),
      getEvidence(),
    ]);

    const [reportResult, documentResult, summaryResult, evidenceResult] = results;

    if (reportResult.status === "fulfilled") {
      setReports(normalizeArray(reportResult.value, "reports"));
    }

    if (documentResult.status === "fulfilled") {
      setDocuments(normalizeArray(documentResult.value, "documents"));
    }

    if (summaryResult.status === "fulfilled") {
      setEvidenceSummary((current) => ({
        ...current,
        ...(summaryResult.value || {}),
      }));
    }

    if (evidenceResult.status === "fulfilled") {
      setEvidence(normalizeArray(evidenceResult.value, "evidence"));
    }

    const failedRequests = results.filter((result) => result.status === "rejected");

    if (failedRequests.length === results.length) {
      setError(
        failedRequests[0]?.reason?.message ||
          "Customer dashboard data could not be loaded."
      );
    } else if (failedRequests.length > 0) {
      setError(
        "Some dashboard metrics are temporarily unavailable. The available data is shown below."
      );
    }

    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const reportStats = useMemo(() => {
    const normalizedReports = Array.isArray(reports) ? reports : [];

    return {
      total: normalizedReports.length,
      draft: normalizedReports.filter(
        (report) => !report.reviewStatus || report.reviewStatus === "draft"
      ).length,
      inReview: normalizedReports.filter(
        (report) => report.reviewStatus === "in_review"
      ).length,
      approved: normalizedReports.filter(
        (report) => report.reviewStatus === "approved"
      ).length,
      published: normalizedReports.filter(
        (report) => report.reviewStatus === "published"
      ).length,
      active: normalizedReports.filter(
        (report) => report.reviewStatus !== "published"
      ).length,
    };
  }, [reports]);

  const reportingReadiness = useMemo(() => {
    const scores = reports
      .map(getReportScore)
      .filter((score) => Number.isFinite(score));

    if (scores.length === 0) return 0;

    return Math.round(
      scores.reduce((sum, score) => sum + score, 0) / scores.length
    );
  }, [reports]);

  const recentReports = useMemo(
    () =>
      [...reports]
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        )
        .slice(0, 5),
    [reports]
  );

  const recentDocuments = useMemo(
    () =>
      [...documents]
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        )
        .slice(0, 5),
    [documents]
  );

  const recentActivity = useMemo(() => {
    const reportActivity = reports.map((report) => ({
      id: `report-${report._id || report.id}`,
      type: "Report",
      title: `${report.companyName || "ESG report"} ${
        report.reportingYear ? `(${report.reportingYear})` : ""
      }`.trim(),
      detail: `Status: ${formatStatus(report.reviewStatus)}`,
      date: report.updatedAt || report.createdAt,
    }));

    const documentActivity = documents.map((document) => ({
      id: `document-${document._id || document.id}`,
      type: "Document",
      title: document.originalName || document.filename || "Uploaded document",
      detail: `${getDocumentDisplayType(document)} · ${formatStatus(
        document.status || "uploaded"
      )}`,
      date: document.updatedAt || document.createdAt,
    }));

    const evidenceActivity = evidence.map((item) => ({
      id: `evidence-${item._id || item.id}`,
      type: "Evidence",
      title: `${item.esrsStandard || "ESRS"} ${
        item.disclosure || "evidence"
      }`.trim(),
      detail: `${formatStatus(item.status)} · ${Number(
        item.confidence || 0
      )}% confidence`,
      date: item.updatedAt || item.createdAt,
    }));

    return [...reportActivity, ...documentActivity, ...evidenceActivity]
      .filter((item) => item.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 8);
  }, [documents, evidence, reports]);

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Customer success</p>
          <h1 style={styles.title}>Customer Dashboard</h1>
          <p style={styles.subtitle}>
            Monitor customer ESG reporting, documents, evidence readiness, and
            reporting progress from one workspace.
          </p>
          {lastUpdated && (
            <p style={styles.lastUpdated}>
              Last updated {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={loadDashboard}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            ...(loading ? styles.disabledButton : {}),
          }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      {error && <div style={styles.warning}>{error}</div>}

      <section style={styles.metricsGrid}>
        <MetricCard
          label="Active Reports"
          value={loading ? "…" : reportStats.active}
          description={`${reportStats.total} total · ${reportStats.published} published`}
        />

        <MetricCard
          label="Evidence Items"
          value={loading ? "…" : evidenceSummary.total || evidence.length}
          description={`${evidenceSummary.approved || 0} approved · ${
            evidenceSummary.published || 0
          } published`}
        />

        <MetricCard
          label="Documents"
          value={loading ? "…" : documents.length}
          description={`${documents.filter((item) => item.status === "completed").length} analysed`}
        />

        <MetricCard
          label="Reporting Readiness"
          value={loading ? "…" : `${reportingReadiness}%`}
          description="Average ESG score across reports"
        />

        <MetricCard
          label="Average Confidence"
          value={loading ? "…" : `${evidenceSummary.averageConfidence || 0}%`}
          description="AI-extracted evidence confidence"
        />
      </section>

      <section style={styles.contentGrid}>
        <Card title="Reporting Workflow" subtitle="Current report lifecycle status">
          {reportStats.total === 0 && !loading ? (
            <EmptyMessage
              title="No reports yet"
              description="Create an ESG report to begin tracking reporting readiness."
            />
          ) : (
            <div style={styles.workflowGrid}>
              <WorkflowItem label="Draft" value={reportStats.draft} tone="neutral" />
              <WorkflowItem label="In Review" value={reportStats.inReview} tone="warning" />
              <WorkflowItem label="Approved" value={reportStats.approved} tone="success" />
              <WorkflowItem label="Published" value={reportStats.published} tone="live" />
            </div>
          )}
        </Card>

        <Card title="Evidence Readiness" subtitle="Governance and publication status">
          <div style={styles.evidenceGrid}>
            <EvidenceMetric
              label="Pending Review"
              value={evidenceSummary.pendingReview || 0}
            />
            <EvidenceMetric label="Approved" value={evidenceSummary.approved || 0} />
            <EvidenceMetric label="Attached" value={evidenceSummary.attached || 0} />
            <EvidenceMetric label="Published" value={evidenceSummary.published || 0} />
          </div>
        </Card>
      </section>

      <section style={styles.contentGrid}>
        <Card title="Recent Reports" subtitle="Latest customer reporting records">
          {recentReports.length === 0 && !loading ? (
            <EmptyMessage
              title="No recent reports"
              description="New and updated reports will appear here."
            />
          ) : (
            <div style={styles.list}>
              {recentReports.map((report) => (
                <div key={report._id || report.id} style={styles.listRow}>
                  <div style={styles.listText}>
                    <strong style={styles.itemTitle}>
                      {report.companyName || "Untitled report"}
                    </strong>
                    <span style={styles.itemMeta}>
                      {report.reportingYear || "Year not set"} · Updated {safeDate(
                        report.updatedAt || report.createdAt
                      )}
                    </span>
                  </div>
                  <StatusBadge status={report.reviewStatus} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recent Documents" subtitle="Latest uploads and analysis status">
          {recentDocuments.length === 0 && !loading ? (
            <EmptyMessage
              title="No recent documents"
              description="Uploaded customer documents will appear here."
            />
          ) : (
            <div style={styles.list}>
              {recentDocuments.map((document) => (
                <div key={document._id || document.id} style={styles.listRow}>
                  <div style={styles.listText}>
                    <strong style={styles.itemTitle}>
                      {document.originalName || document.filename || "Document"}
                    </strong>
                    <span style={styles.itemMeta}>
                      {getDocumentDisplayType(document)} · {safeDate(
                        document.updatedAt || document.createdAt
                      )}
                    </span>
                  </div>
                  <StatusBadge status={document.status || "uploaded"} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      <Card title="Recent Customer Activity" subtitle="Latest report, document, and evidence events">
        {recentActivity.length === 0 && !loading ? (
          <EmptyMessage
            title="No recent activity"
            description="Customer activity will appear as teams work in Ecovanta."
          />
        ) : (
          <div style={styles.activityList}>
            {recentActivity.map((activity) => (
              <div key={activity.id} style={styles.activityItem}>
                <div style={styles.activityIcon}>
                  {activity.type === "Report"
                    ? "R"
                    : activity.type === "Document"
                    ? "D"
                    : "E"}
                </div>
                <div style={styles.listText}>
                  <strong style={styles.itemTitle}>{activity.title}</strong>
                  <span style={styles.itemMeta}>
                    {activity.type} · {activity.detail}
                  </span>
                </div>
                <time style={styles.activityDate}>{safeDate(activity.date)}</time>
              </div>
            ))}
          </div>
        )}
      </Card>
    </main>
  );
}

function MetricCard({ label, value, description }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
      <span style={styles.metricDescription}>{description}</span>
    </div>
  );
}

function WorkflowItem({ label, value, tone }) {
  const toneStyles = {
    neutral: { background: "#f1f5f9", color: "#475569" },
    warning: { background: "#fef3c7", color: "#92400e" },
    success: { background: "#dcfce7", color: "#166534" },
    live: { background: "#ccfbf1", color: "#0f766e" },
  };

  return (
    <div style={styles.workflowItem}>
      <span style={{ ...styles.workflowBadge, ...toneStyles[tone] }}>{label}</span>
      <strong style={styles.workflowValue}>{value}</strong>
    </div>
  );
}

function EvidenceMetric({ label, value }) {
  return (
    <div style={styles.evidenceMetric}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.evidenceValue}>{value}</strong>
    </div>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || "draft").toLowerCase();
  const palette =
    normalized === "published" || normalized === "completed"
      ? { background: "#dcfce7", color: "#166534" }
      : normalized === "approved"
      ? { background: "#ccfbf1", color: "#0f766e" }
      : normalized === "in_review" || normalized === "processing"
      ? { background: "#fef3c7", color: "#92400e" }
      : normalized === "failed" || normalized === "rejected"
      ? { background: "#fee2e2", color: "#991b1b" }
      : { background: "#f1f5f9", color: "#475569" };

  return (
    <span style={{ ...styles.statusBadge, ...palette }}>
      {formatStatus(status)}
    </span>
  );
}

function EmptyMessage({ title, description }) {
  return (
    <div style={styles.emptyState}>
      <strong style={styles.emptyTitle}>{title}</strong>
      <span style={styles.emptyDescription}>{description}</span>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gap: "20px",
    padding: "8px 0 32px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
  },
  eyebrow: {
    margin: "0 0 8px",
    color: "#0f766e",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "36px",
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },
  subtitle: {
    maxWidth: "850px",
    margin: "10px 0 0",
    color: "#64748b",
    fontSize: "16px",
    lineHeight: 1.7,
  },
  lastUpdated: {
    margin: "8px 0 0",
    color: "#94a3b8",
    fontSize: "12px",
  },
  refreshButton: {
    flexShrink: 0,
    padding: "10px 16px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  disabledButton: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  warning: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid #fde68a",
    background: "#fffbeb",
    color: "#92400e",
    fontSize: "13px",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "16px",
  },
  metricCard: {
    display: "grid",
    gap: "8px",
    padding: "20px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  metricValue: {
    color: "#0f172a",
    fontSize: "30px",
    fontWeight: 900,
  },
  metricDescription: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: "20px",
    alignItems: "start",
  },
  workflowGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "10px",
  },
  workflowItem: {
    display: "grid",
    gap: "10px",
    padding: "14px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    background: "#f8fafc",
  },
  workflowBadge: {
    display: "inline-flex",
    width: "fit-content",
    padding: "4px 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
  },
  workflowValue: {
    color: "#0f172a",
    fontSize: "24px",
  },
  evidenceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  evidenceMetric: {
    display: "grid",
    gap: "8px",
    padding: "16px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    background: "#f8fafc",
  },
  evidenceValue: {
    color: "#0f172a",
    fontSize: "24px",
  },
  list: {
    display: "grid",
  },
  listRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "13px 0",
    borderBottom: "1px solid #eef2f7",
  },
  listText: {
    minWidth: 0,
    display: "grid",
    gap: "4px",
  },
  itemTitle: {
    color: "#0f172a",
    fontSize: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemMeta: {
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  statusBadge: {
    flexShrink: 0,
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
  },
  activityList: {
    display: "grid",
  },
  activityItem: {
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: "12px",
    padding: "13px 0",
    borderBottom: "1px solid #eef2f7",
  },
  activityIcon: {
    width: "32px",
    height: "32px",
    display: "grid",
    placeItems: "center",
    borderRadius: "10px",
    background: "#ecfdf5",
    color: "#0f766e",
    fontSize: "12px",
    fontWeight: 900,
  },
  activityDate: {
    color: "#94a3b8",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  emptyState: {
    display: "grid",
    gap: "6px",
    padding: "22px 4px",
    textAlign: "center",
  },
  emptyTitle: {
    color: "#0f172a",
    fontSize: "14px",
  },
  emptyDescription: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },
};
